import React from 'react';
import { Select, select, t, Trans } from '@lingui/macro';

import FrequentlyAskedQuestions from '@rover/components/src/FrequentlyAskedQuestions';
import { Link, Paragraph } from '@rover/kibble/core';

import { ServiceFaq } from './constants';
import FirstFAQIsOpen from './FirstFAQIsOpen';
import FrequentlyAskedQuestion from './FrequentlyAskedQuestion';
import UnorderedList, { ListItem } from './UnorderedList';
import useFacts from './useFacts';

const HouseSittingFaqs: React.FC<ServiceFaq> = ({
  localizedLinkUrls: links,
  facts,
  locationName,
  localityType,
  htmlRef,
}) => {
  // we create these two variables so translators can distinguish between them in the lingui selects
  const cityName = locationName;
  const neighborhoodName = locationName;

  const {
    avgRepeatCustomerAmongSittersWithRepeatNum,
    maxSearchableSittersNum,
    medianListPricePerUnitLocalCurrencyNum,
    sittersWithMinResponseTimeIn60minPct,
    yearsOfExperienceNum,
  } = useFacts(facts);

  return (
    <FrequentlyAskedQuestions
      htmlRef={htmlRef}
      data-testid="house-sitting-faqs"
      mt={['6x', '16x']}
      pb={['4x', '8x']}
    >
      <FrequentlyAskedQuestions.Header webProps={{ responsive: false }}>
        <Trans>House Sitting FAQs</Trans>
      </FrequentlyAskedQuestions.Header>
      <FrequentlyAskedQuestions.Subheader>
        <Trans>Frequently asked questions about Rover</Trans>
      </FrequentlyAskedQuestions.Subheader>
      <FirstFAQIsOpen>
        {/* 1 */}
        {medianListPricePerUnitLocalCurrencyNum &&
          'whatAreTheServiceFees' in links &&
          (() => {
            const { yearMonthLastUpdated, asCurrency: formattedAmount } =
              medianListPricePerUnitLocalCurrencyNum;
            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How much does it cost to hire a house sitter in ${cityName}?`,
                  neighborhood: `How much does it cost to hire a house sitter in ${neighborhoodName}?`,
                  other: `How much does it cost to hire a ${locationName} house sitter?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        House sitters on Rover are free to set their rates. The median cost to hire
                        a house sitter in {cityName} on Rover as of {yearMonthLastUpdated} was about{' '}
                        {formattedAmount} per night, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A house sitter’s rate may also change as you customize your booking to fit
                        your needs.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        House sitters on Rover are free to set their rates. The median cost to hire
                        a house sitter in {neighborhoodName} on Rover as of {yearMonthLastUpdated}{' '}
                        was about {formattedAmount} per night, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A house sitter’s rate may also change as you customize your booking to fit
                        your needs.
                      </Trans>
                    }
                    other={
                      <Trans>
                        House sitters on Rover are free to set their rates. The median cost to hire
                        a {locationName} house sitter on Rover as of {yearMonthLastUpdated} was
                        about {formattedAmount} per night, after factoring in{' '}
                        <Link href={links.whatAreTheServiceFees} target="_blank">
                          Rover’s service fees
                        </Link>
                        . A house sitter’s rate may also change as you customize your booking to fit
                        your needs.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 2 */}
        {'howDoIContactAPetSitterOrDogWalkerRoverApp' in links &&
          'howDoIContactAPetSitterOrDogWalkerComputer' in links && (
            <FrequentlyAskedQuestion question={t`How do I contact a ${locationName} house sitter?`}>
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <Trans>
                    If you’re searching for a {locationName} house sitter for the first time, visit
                    the house sitter’s profile and select the Contact button.
                  </Trans>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    If you have an active request or have booked a service with a house sitter
                    before, learn more about how to do this in the{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerRoverApp} target="_blank">
                      Rover app
                    </Link>{' '}
                    or on your{' '}
                    <Link href={links.howDoIContactAPetSitterOrDogWalkerComputer} target="_blank">
                      computer
                    </Link>
                    .
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}

        {/* 3 */}
        {maxSearchableSittersNum &&
          (() => {
            const { yearMonthLastUpdated, asNumber: houseSittersCount } = maxSearchableSittersNum;
            return (
              <FrequentlyAskedQuestion
                question={select(localityType, {
                  city: `How many house sitters are in ${cityName}?`,
                  neighborhood: `How many house sitters are in ${neighborhoodName}?`,
                  other: `How many ${locationName} house sitters are there?`,
                })}
              >
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        As of {yearMonthLastUpdated}, there are {houseSittersCount} house sitters in{' '}
                        {cityName}. You can filter, sort, expand your radius, read reviews, and
                        compare pricing to find the perfect house sitter near you. As a reminder,
                        house sitters joining Rover must pass a background check for you and your
                        house’s safety.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        As of {yearMonthLastUpdated}, there are {houseSittersCount} house sitters in{' '}
                        {neighborhoodName}. You can filter, sort, expand your radius, read reviews,
                        and compare pricing to find the perfect house sitter near you. As a
                        reminder, house sitters joining Rover must pass a background check for you
                        and your house’s safety.
                      </Trans>
                    }
                    other={
                      <Trans>
                        As of {yearMonthLastUpdated}, there are {houseSittersCount} {locationName}{' '}
                        house sitters. You can filter, sort, expand your radius, read reviews, and
                        compare pricing to find the perfect house sitter near you. As a reminder,
                        house sitters joining Rover must pass a background check for you and your
                        house’s safety.
                      </Trans>
                    }
                  />
                </Paragraph>
              </FrequentlyAskedQuestion>
            );
          })()}

        {/* 4 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How long will it take for me to find a house sitter in ${cityName}?`,
            neighborhood: `How long will it take for me to find a house sitter in ${neighborhoodName}?`,
            other: `How long will it take for me to find a ${locationName} house sitter?`,
          })}
        >
          {sittersWithMinResponseTimeIn60minPct ? (
            (() => {
              const percent = sittersWithMinResponseTimeIn60minPct.asPercent;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    Rover makes it easy to reach out to multiple house sitters about your booking.
                    Typically, {percent} of {locationName} house sitters respond in under an hour.
                  </Trans>
                </Paragraph>
              );
            })()
          ) : (
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                Rover makes it easy to reach out to multiple house sitters about your booking.
                Typically, house sitters on Rover respond in under an hour.
              </Trans>
            </Paragraph>
          )}
        </FrequentlyAskedQuestion>

        {/* 5 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `How experienced are house sitters in ${cityName}?`,
            neighborhood: `How experienced are house sitters in ${neighborhoodName}?`,
            other: `How experienced are ${locationName} house sitters?`,
          })}
        >
          {(() => {
            if (yearsOfExperienceNum && avgRepeatCustomerAmongSittersWithRepeatNum) {
              const yearsOfExperience = yearsOfExperienceNum.raw;
              const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        House sitters in {cityName} have an average of {yearsOfExperience} years of
                        experience. House sitters with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from house sitter
                        to house sitter, but you can view reviews, years of experience, and number
                        of repeat clients when you compare house sitters in {cityName}.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        House sitters in {neighborhoodName} have an average of {yearsOfExperience}{' '}
                        years of experience. House sitters with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from house sitter
                        to house sitter, but you can view reviews, years of experience, and number
                        of repeat clients when you compare house sitters in {neighborhoodName}.
                      </Trans>
                    }
                    other={
                      <Trans>
                        {locationName} house sitters have an average of {yearsOfExperience} years of
                        experience. House sitters with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from house sitter
                        to house sitter, but you can view reviews, years of experience, and number
                        of repeat clients when you compare house sitters.
                      </Trans>
                    }
                  />
                </Paragraph>
              );
            }

            if (avgRepeatCustomerAmongSittersWithRepeatNum) {
              const repeatCustomersCount = avgRepeatCustomerAmongSittersWithRepeatNum.asNumber;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        House sitters with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from house sitter
                        to house sitter, but you can view reviews, years of experience, and number
                        of repeat clients when you compare house sitters in {cityName}.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        House sitters with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from house sitter
                        to house sitter, but you can view reviews, years of experience, and number
                        of repeat clients when you compare house sitters in {neighborhoodName}.
                      </Trans>
                    }
                    other={
                      <Trans>
                        House sitters with repeat customers have an average of{' '}
                        {repeatCustomersCount} repeat clients. Experience can vary from house sitter
                        to house sitter, but you can view reviews, years of experience, and number
                        of repeat clients when you compare house sitters.
                      </Trans>
                    }
                  />
                </Paragraph>
              );
            }

            if (yearsOfExperienceNum) {
              const yearsOfExperience = yearsOfExperienceNum.raw;
              return (
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Select
                    value={localityType}
                    _city={
                      <Trans>
                        House sitters in {cityName} have an average of {yearsOfExperience} years of
                        experience. This can vary widely from house sitter to house sitter, but you
                        can view reviews, years of experience, and number of repeat clients when
                        comparing house sitters available in {cityName}.
                      </Trans>
                    }
                    _neighborhood={
                      <Trans>
                        House sitters in {neighborhoodName} have an average of {yearsOfExperience}{' '}
                        years of experience. This can vary widely from house sitter to house sitter,
                        but you can view reviews, years of experience, and number of repeat clients
                        when comparing house sitters available in {neighborhoodName}.
                      </Trans>
                    }
                    other={
                      <Trans>
                        {locationName} house sitters have an average of {yearsOfExperience} years of
                        experience. This can vary widely from house sitter to house sitter, but you
                        can view reviews, years of experience, and number of repeat clients when
                        comparing house sitters.
                      </Trans>
                    }
                  />
                </Paragraph>
              );
            }

            return (
              <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                <Select
                  value={localityType}
                  _city={
                    <Trans>
                      Experience can vary widely from house sitter to house sitter, but you can view
                      reviews, years of experience, and number of repeat clients when comparing
                      house sitters available in {cityName}.
                    </Trans>
                  }
                  _neighborhood={
                    <Trans>
                      Experience can vary widely from house sitter to house sitter, but you can view
                      reviews, years of experience, and number of repeat clients when comparing
                      house sitters available in {neighborhoodName}.
                    </Trans>
                  }
                  other={
                    <Trans>
                      Experience can vary widely from house sitter to house sitter, but you can view
                      reviews, years of experience, and number of repeat clients when comparing
                      house sitters.
                    </Trans>
                  }
                />
              </Paragraph>
            );
          })()}
        </FrequentlyAskedQuestion>

        {/* 6 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `What sort of services do house sitters in ${cityName} offer on Rover?`,
            neighborhood: `What sort of services do house sitters in ${neighborhoodName} offer on Rover?`,
            other: `What sort of services do ${locationName} house sitters offer on Rover?`,
          })}
        >
          <>
            <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
              <Trans>
                Heading out of town? It’s easy to book a 5-star house sitter to take care of your
                home. Book a house sitter to take care of your dog and cat, while keeping an eye on
                your property. And the best part? Your best friend gets to stay on their home turf.{' '}
                {locationName} house sitters are great for:
              </Trans>
            </Paragraph>
            <UnorderedList>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Dogs who’d prefer to stay on their home turf
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Cats and caged pets
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Pet parents on a tight schedule
                </Trans>
              </ListItem>
              <ListItem>
                <Trans comment="Part of a list describing who would benefit from a particular service type">
                  Keeping an eye on your home and plants while you’re away
                </Trans>
              </ListItem>
            </UnorderedList>
          </>
        </FrequentlyAskedQuestion>

        {/* 7 */}
        <FrequentlyAskedQuestion
          question={select(localityType, {
            city: `Do house sitters in ${cityName} pass a background check?`,
            neighborhood: `Do house sitters in ${neighborhoodName} pass a background check?`,
            other: `Do ${locationName} house sitters pass a background check?`,
          })}
        >
          <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
            <Trans>
              Yes! House sitters joining Rover must pass a background check before offering their
              services. You can also easily keep in touch with your house sitter through messages on
              Rover for delightful photo updates. The Rover team is available 24/7, and your house
              sitter has access to advice from qualified veterinary professionals. In the rare event
              of any issues during a booking, rest easy knowing your pet is covered by the Rover
              Guarantee reimbursement program for eligible veterinary care.
            </Trans>
          </Paragraph>
        </FrequentlyAskedQuestion>

        {/* 8 */}
        {'howDoICancelABookingAndReceiveARefundRoverApp' in links &&
          'howDoICancelABookingAndReceiveARefundComputer' in links && (
            <FrequentlyAskedQuestion
              question={t`How do I cancel a house sitting service and receive a refund?`}
            >
              <>
                <Paragraph webProps={{ maxWidth: 'none' }}>
                  <i>
                    <Trans>
                      <strong>Note:</strong> If your house sitter requested to cancel this booking,
                      then they need to cancel from their account. Please contact them via{' '}
                      <Link href="/account/inbox/" target="_blank">
                        Rover Messages
                      </Link>
                      .
                    </Trans>
                  </i>
                </Paragraph>
                <Paragraph webProps={{ maxWidth: 'none' }} mb="0x">
                  <Trans>
                    We get it—sometimes plans change and you may need to cancel your house sitting
                    service. You can cancel an upcoming booking directly from the{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundRoverApp}
                      target="_blank"
                    >
                      Rover app
                    </Link>{' '}
                    or your{' '}
                    <Link
                      href={links.howDoICancelABookingAndReceiveARefundComputer}
                      target="_blank"
                    >
                      computer
                    </Link>
                    , in accordance with your house sitter’s cancellation policy.
                  </Trans>
                </Paragraph>
              </>
            </FrequentlyAskedQuestion>
          )}
      </FirstFAQIsOpen>
    </FrequentlyAskedQuestions>
  );
};

export default HouseSittingFaqs;
